import { Box, Button, FormControl, FormLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserIcon, HidePassword, VectorIcon } from '~/assets/icons';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { handleApiError } from '~/utils/helper';
import { ILoginPayload } from '~/utils/interface/auth';
import styles from '../auth.module.scss';
import AuthLayout from '../AuthLayout';
import { useAppDispatch } from '~/redux/hooks';
import { handleLogin, WEARER_WEB_ACCESS_DENIED_ERROR } from '~/thunks/auth/authThunk';
import { useFormik } from 'formik';
import { commonActions } from '~/thunks/common/commonSlice';
import authValidation from '~/validation/auth';

const cx = classNames.bind(styles);

const initialValues: ILoginPayload = {
  username: '',
  password: '',
};
const LoginAdmin = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation('admin/auth');

  const form = useFormik<ILoginPayload>({
    initialValues,
    validateOnChange: false,
    enableReinitialize: true,
    validationSchema: authValidation(t).login,
    onSubmit: async (values) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        await dispatch(handleLogin(values)).unwrap();
        navigate(`${adminRouteAbsolute.dashboard}`);
      } catch (error) {
        if (error === WEARER_WEB_ACCESS_DENIED_ERROR) {
          handleApiError(t('login.wearerWebAccessDenied'));
          return;
        }
        handleApiError(error);
      } finally {
        dispatch(commonActions.setIsLoading(false));
      }
    },
  });

  return (
    <AuthLayout>
      <Typography className='text-center font-bold text-[#123B6D] text-2xl'>{t('login.title')}</Typography>
      <form onSubmit={form.handleSubmit}>
        <Box className={cx('input-custom')}>
          <Box className='my-2'>
            <FormControl fullWidth>
              <FormLabel htmlFor='username' className='my-2 font-semibold text-[#161A1F] text-sm'>
                {`${t('login.username')}`}
              </FormLabel>
              <TextField
                name='username'
                placeholder={t('login.usernamePlaceholder')}
                value={form.values.username}
                onChange={form.handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <UserIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                    },
                  },
                }}
                className='w-full'
                error={Boolean(form.errors?.username)}
                helperText={form.errors?.username}
                FormHelperTextProps={{
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    margin: '2px 0 0',
                    minHeight: '1rem',
                  },
                }}
              />
            </FormControl>
          </Box>
          <Box className='mb-5'>
            <FormControl fullWidth>
              <FormLabel htmlFor='password' className='mb-2 font-semibold text-[#161A1F] text-sm'>
                {`${t('login.password')}`}
              </FormLabel>
              <TextField
                name='password'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                value={form.values.password}
                onChange={form.handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <UserIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={showPassword ? 'hide password' : 'show password'}
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                        edge='end'
                      >
                        {showPassword ? <VectorIcon /> : <HidePassword />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    '& input::-ms-reveal, & input::-ms-clear': {
                      display: 'none',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                    },
                  },
                }}
                className='w-full'
                error={Boolean(form.errors?.password)}
                helperText={form.errors?.password}
                FormHelperTextProps={{
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    margin: '2px 0 0',
                    minHeight: '1rem',
                  },
                }}
              />
            </FormControl>
          </Box>
        </Box>
        <Button
          type='submit'
          disabled={form.isSubmitting}
          className='w-full text-base rounded-xl min-h-[48px] text-white bg-[#123B6D] hover:bg-[#161A1F]'
        >
          {`${t('login.login')}`}
        </Button>
      </form>
    </AuthLayout>
  );
};
export default memo(LoginAdmin);
